import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import { showSectionSelectionModal } from '../../actions/modals'
import { updatePreviewItem } from '../../actions/previewItem'
import SearchProviderContainer from './SearchProviderContainer'
import Button from '../../components/uiKit/Button'
import TopPageHeader from '../../components/pageHeaders/TopPageHeader'
import StickyPageHeader from '../../components/pageHeaders/StickyPageHeader'
import SearchResultsCard from '../../components/search/SearchResultsCard'
import SearchResultsFolderCard from '../../components/search/SearchResultsFolderCard'
import SearchResultsRepoCard from '../../components/search/SearchResultsRepoCard'
import CategoryFilters from '../../components/search/CategoryFilters'
import SearchResultsPreviewPanel from '../../components/search/SearchResultsPreviewPanel'
import Icon from '../../components/misc/Icons'
import { getQuestionBodyLabel } from '../../utils/getQuestionBodyLabel'
import { getDocumentType } from '../../utils/search/getDocumentType'
import { getRepoforId } from '../../utils/getRepoforId'
import { getLinkForRepo } from '../../utils/getLinkForRepo'
import { useSearchContext } from '../../services/search/SearchContext'
import { recordRecentSearch } from '../../utils/search/recentSearches'
import analytics from '../../utils/cdpAnalytics'
import { trackContentUse, trackHelpUse } from '../../services/search/newSearchAnalytics'

const updateAfter = 700
const createURL = (state) => `?${qs.stringify({ query: state.query })}`
const searchStateToUrl = (props, searchState) => (
  searchState ? `${props.location.pathname}${createURL(searchState)}` : ''
)
const urlToSearchState = (location) => qs.parse(location.search.slice(1))

// ─── Search input ──────────────────────────────────────────────────────────────

function SearchInput() {
  const { currentRefinement, refine } = useSearchContext()
  return (
    <input
      type='text'
      placeholder='Search content'
      value={currentRefinement}
      onChange={(e) => refine(e.target.value)}
      autoComplete='off'
      className='topBar-search'
      id='searchInputField'
      onKeyDown={(e) => {
        // Ignore the Enter that confirms an IME composition (e.g. Japanese
        // kanji selection) — keyCode 229 / isComposing — so it commits the
        // candidate instead of blurring the input.
        if (e.keyCode === 229 || (e.nativeEvent && e.nativeEvent.isComposing)) return
        if (e.keyCode === 13) {
          // Any query committed directly on the results page is a content search.
          recordRecentSearch(currentRefinement)
          document.getElementById('searchInputField').blur()
        }
      }}
    />
  )
}

// ─── Router history bridge (react-router v4 — no useHistory) ──────────────────

const RouterHistoryContext = React.createContext({ history: { push: () => {} } })
const RouterHistoryBridge = withRouter(({ history, children }) => (
  <RouterHistoryContext.Provider value={{ history }}>
    {children}
  </RouterHistoryContext.Provider>
))
function useRouterHistory() {
  return React.useContext(RouterHistoryContext)
}

// ─── Category-filter context ──────────────────────────────────────────────────
// Lives on SearchResultsContainer's state. Descendants (the header pill bar
// and the per-category groups) subscribe via useCategoryFilters().

const DEFAULT_CATEGORY_FILTERS = {
  enabled: {
    repos: true, folders: true, setsQuestions: true,
  },
  onToggle: () => {},
}
const CategoryFiltersContext = React.createContext(DEFAULT_CATEGORY_FILTERS)
function useCategoryFilters() {
  return React.useContext(CategoryFiltersContext)
}

// ─── Active-result context ────────────────────────────────────────────────────
// Tracks which row in the (flattened) results list is currently active so
// keyboard navigation, the highlight class, and the right-column preview can
// stay in sync.

const ActiveResultContext = React.createContext({
  activeId: null,
  activeHit: null,
  flatHits: [],
  activateRow: () => {},
})
function useActiveResult() {
  return React.useContext(ActiveResultContext)
}

// ─── Section group header (Library) ───────────────────────────────────────────
// Rendered like the dropdown sectionHeader: icon on the left, uppercase label.

// The big "Library" group header is intentionally hidden (kept in code for
// possible future use). Flip to true to bring it back.
const SHOW_LIBRARY_GROUP_HEADER = false

function SectionGroupHeader({ icon, label }) {
  return (
    <div className='searchResults-sectionGroupHeader'>
      <div className='searchResults-sectionGroupHeader-icon'>
        <Icon name={icon} />
      </div>
      <span className='searchResults-sectionGroupHeader-label'>{label}</span>
    </div>
  )
}

// ─── Individual hit renderer ───────────────────────────────────────────────────

// Stable id used in the flat-hits list and the active-row wrapper. `source`
// disambiguates the same logical item appearing across Algolia + local
// engines so each row in the visible list still has a unique key.
function buildHitRowId(source, hit) {
  const id = (hit && hit.objectID) || (hit && hit.id) || ''
  return `${source}-${id}`
}

// Highlight + auto-scroll wrapper around each result card. Hovering a row makes
// it the single active row (same model as the dropdown's react-autosuggest),
// so the keyboard highlight and the mouse never show two highlighted rows at
// once. Rows behind the pinned footer can't be hovered (the fixed bar sits on
// top), so the active row is always fully visible — the scrollIntoView below is
// a no-op on hover and only does real work for keyboard navigation.
function ActiveResultRow({ rowId, onNewTab, children }) {
  const { activeId, activateRow } = useActiveResult()
  const ref = React.useRef(null)
  const isActive = activeId === rowId
  React.useEffect(() => {
    if (isActive && ref.current && typeof ref.current.scrollIntoView === 'function') {
      ref.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isActive])
  // Middle click opens the row in a new tab, like a link would; the mousedown
  // handler is only there to suppress the browser's autoscroll cursor.
  const onMouseDown = (e) => { if (e.button === 1) e.preventDefault() }
  const onAuxClick = (e) => {
    if (e.button !== 1 || !onNewTab) return
    e.preventDefault()
    onNewTab()
  }
  return (
    <div
      ref={ref}
      className={`searchResults-rowWrapper${isActive ? ' searchResults-rowWrapper--active' : ''}`}
      onMouseEnter={() => { if (activateRow) activateRow(rowId) }}
      onMouseDown={onMouseDown}
      onAuxClick={onAuxClick}
    >
      {children}
    </div>
  )
}

// Delay used to distinguish a single click from the first click of a double
// click — a single click is deferred this long and cancelled if a second click
// (double click) arrives.
const ROW_CLICK_DELAY = 220

const HitComponent = ({
  item, play, rowId, history, updatePreviewItem: dispatchUpdatePreviewItem,
}) => {
  const itemType = getDocumentType(item)
  const { activateRow } = useActiveResult()
  const { currentRefinement } = useSearchContext()
  const clickTimer = React.useRef(null)
  const itemId = item && (item.id || item._id)
  const isPreviewable = itemType === 'set' || itemType === 'question'

  const openDirect = React.useCallback(() => {
    const path = getPathForHit(itemType, item)
    trackContentUse({
      source: 'searchResults', query: currentRefinement, action: 'open', itemType, itemId,
    })
    if (path) history.push(path)
  }, [itemType, item, history, currentRefinement, itemId])

  // Cmd/Ctrl click and middle click open the item in a new tab, matching the
  // link behaviour these rows had before they became click-handled divs.
  const openInNewTab = React.useCallback(() => {
    const path = getPathForHit(itemType, item)
    if (!path) return
    trackContentUse({
      source: 'searchResults', query: currentRefinement, action: 'openNewTab', itemType, itemId,
    })
    window.open(path, '_blank', 'noopener,noreferrer')
  }, [itemType, item, currentRefinement, itemId])

  const handleClick = React.useCallback((e) => {
    // Read the modifiers before the timer below: React 16 pools synthetic
    // events, so the event is recycled by the time the timeout runs.
    if (e && (e.metaKey || e.ctrlKey)) {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current)
        clickTimer.current = null
      }
      openInNewTab()
      return
    }
    if (activateRow) activateRow(rowId)
    if (clickTimer.current) clearTimeout(clickTimer.current)
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null
      // Single click: set/question open the lightweight preview modal; packs
      // and folders (no modal) navigate directly.
      if (isPreviewable && itemId) {
        dispatchUpdatePreviewItem(itemId, 'searchResults')
      } else {
        openDirect()
      }
    }, ROW_CLICK_DELAY)
  }, [
    activateRow, rowId, isPreviewable, itemId, dispatchUpdatePreviewItem,
    openDirect, openInNewTab,
  ])

  const handleDoubleClick = React.useCallback(() => {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
    // Double click always opens the item.
    openDirect()
  }, [openDirect])

  React.useEffect(() => () => {
    if (clickTimer.current) clearTimeout(clickTimer.current)
  }, [])

  let card = null
  if (itemType === 'set') {
    card = (
      <SearchResultsCard
        set
        item={item}
        title={item.name}
        play={() => { play(item) }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (itemType === 'question') {
    card = (
      <SearchResultsCard
        question
        item={item}
        title={getQuestionBodyLabel(item)}
        play={() => { play(item) }}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (itemType === 'folder') {
    card = (
      <SearchResultsFolderCard
        folder={item}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  } else if (itemType === 'repo') {
    card = (
      <SearchResultsRepoCard
        repo={item}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    )
  }
  if (!card) return null
  return <ActiveResultRow rowId={rowId} onNewTab={openInNewTab}>{card}</ActiveResultRow>
}

const Hit = withRouter(connect(null, { updatePreviewItem })(HitComponent))

function HitList({ hits, source, play }) {
  if (!hits || hits.length === 0) return null
  return (
    <div className='searchResults-list'>
      {hits.map((hit) => (
        <Hit
          item={hit}
          key={buildHitRowId(source, hit)}
          rowId={buildHitRowId(source, hit)}
          play={play}
        />
      ))}
    </div>
  )
}

// ─── Category header within Library (PACKS, FOLDERS, SETS, QUESTIONS) ─────────

function CategoryHeader({ label }) {
  return <div className='searchResults-categoryHeader'>{label}</div>
}

// ─── Generic library category ─────────────────────────────────────────────────
// Renders one category (e.g. "Packs") from Algolia results only. Local content
// engines (Fuse/MiniSearch) run as shadows and are not shown here.

function LibraryCategory({
  label,
  algoliaHits,
  algoliaHasMore,
  algoliaLoadMore,
  play,
}) {
  const { currentRefinement } = useSearchContext()
  if (!currentRefinement || algoliaHits.length === 0) return null
  return (
    <div className='searchResults-categoryGroup'>
      <CategoryHeader label={label} />
      <HitList hits={algoliaHits} source='a' play={play} />
      {algoliaHasMore && (
        <div className='searchResults-list-showMoreContainer'>
          <Button label='Show more results' size='large' onClickFunction={algoliaLoadMore} />
        </div>
      )}
    </div>
  )
}

// ─── Per-category wrappers ─────────────────────────────────────────────────────

function PacksGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.repos || {}
  return (
    <LibraryCategory
      label='Packs'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      play={play}
    />
  )
}

function FoldersGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.folders || {}
  return (
    <LibraryCategory
      label='Folders'
      algoliaHits={algo.hits || []}
      algoliaHasMore={!!algo.hasMore}
      algoliaLoadMore={algo.loadMore}
      play={play}
    />
  )
}

function SetsQuestionsGroup({ play }) {
  const { indexResults } = useSearchContext()
  const algo = indexResults.sets_questions || {}
  // Sets and questions share one Algolia index; render them mixed in the
  // order Algolia returned them (no per-type splitting/sorting).
  const isSetOrQuestion = (h) => {
    const itemType = getDocumentType(h)
    return itemType === 'set' || itemType === 'question'
  }
  return (
    <LibraryCategory
      label='Content Items'
      algoliaHits={(algo.hits || []).filter(isSetOrQuestion)}
      algoliaHasMore={false}
      algoliaLoadMore={null}
      play={play}
    />
  )
}

// ─── Library section ───────────────────────────────────────────────────────────

function LibrarySection({ play }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { enabled } = useCategoryFilters()
  const algoliaAny = (
    (indexResults.repos?.hitCount || 0) +
    (indexResults.folders?.hitCount || 0) +
    (indexResults.sets_questions?.hitCount || 0)
  ) > 0
  const anyEnabled = enabled.repos || enabled.folders || enabled.setsQuestions
  if (!currentRefinement || !algoliaAny || !anyEnabled) return null
  return (
    <React.Fragment>
      {SHOW_LIBRARY_GROUP_HEADER && <SectionGroupHeader icon='library' label='Library' />}
      <div className='searchResults-library'>
        {enabled.repos && <PacksGroup play={play} />}
        {enabled.folders && <FoldersGroup play={play} />}
        {enabled.setsQuestions && <SetsQuestionsGroup play={play} />}
      </div>
    </React.Fragment>
  )
}

// ─── Page header ──────────────────────────────────────────────────────────────

// Count `sets_questions` Algolia hits that are sets or questions (one merged
// category). Other types, if any, are excluded.
function countSetsQuestions(indexResults) {
  const isSetOrQuestion = (h) => {
    const itemType = getDocumentType(h)
    return itemType === 'set' || itemType === 'question'
  }
  const algoliaHits = (indexResults.sets_questions && indexResults.sets_questions.hits) || []
  return algoliaHits.filter(isSetOrQuestion).length
}

function countSimpleCategory(indexResults, indexName) {
  return (indexResults[indexName]?.hitCount) || 0
}

function ResultsPageHeader() {
  const { indexResults, currentRefinement } = useSearchContext()
  const { enabled, onToggle } = useCategoryFilters()

  const categoryCounts = {
    repos: countSimpleCategory(indexResults, 'repos'),
    folders: countSimpleCategory(indexResults, 'folders'),
    setsQuestions: countSetsQuestions(indexResults),
  }
  // Content-only count: the page only renders Library (Algolia) content rows,
  // so the counter reflects exactly those. Local-only matches (e.g. navigation)
  // are intentionally excluded.
  const total = Object.keys(categoryCounts).reduce(
    (acc, key) => acc + (enabled[key] ? categoryCounts[key] : 0), 0,
  )

  let searchResultsText
  if (currentRefinement) {
    if (total === 1) searchResultsText = '1 search result'
    else if (total > 1) searchResultsText = `${total} search results`
    else searchResultsText = 'No Results'
  }

  const headerRow = currentRefinement ? (
    <div className='searchResults-headerRow'>
      <span className='searchResults-headerRow-count'>{searchResultsText}</span>
      <CategoryFilters enabled={enabled} onToggle={onToggle} />
    </div>
  ) : searchResultsText

  return [
    <TopPageHeader
      key='topHeader'
      location='searchResults'
      header={<SearchInput />}
      headerInfoRow={headerRow}
    />,
    <StickyPageHeader
      key='stickyHeader'
      location='searchResults'
      header={currentRefinement}
      actionGroup={headerRow}
    />,
  ]
}

// ─── Flat-list builder + keyboard nav controller ─────────────────────────────

function getPathForHit(rowType, hit) {
  if (rowType === 'nav') return hit && hit.path
  if (rowType === 'external') return hit && hit.url
  if (rowType === 'repo') return getLinkForRepo(hit)
  if (rowType === 'folder') {
    if (!hit) return null
    if (hit.repo) {
      const parentRepo = getRepoforId(hit.repo)
      if (parentRepo) return `${getLinkForRepo(parentRepo)}/${hit.id}`
      return `/packs/${hit.repo}/${hit.id}`
    }
    return `/library/${hit.id}`
  }
  if (rowType === 'set') {
    const id = hit && (hit.id || hit._id)
    return id ? `/set/${id}` : null
  }
  if (rowType === 'question') {
    const id = hit && (hit.id || hit._id)
    return id ? `/question/${id}` : null
  }
  return null
}

function appendLibraryRows(out, source, hits) {
  if (!Array.isArray(hits)) return
  hits.forEach((hit) => {
    const itemType = getDocumentType(hit)
    if (!itemType) return
    out.push({
      id: buildHitRowId(source, hit),
      itemType,
      hit,
      path: getPathForHit(itemType, hit),
      external: false,
    })
  })
}

// Builds the flat ordered list of currently-visible result rows, in the same
// order the page renders them: the Library categories in fixed order (Packs,
// Folders, Sets + Questions) — Algolia rows only. Sets and questions are mixed
// in Algolia order. Mirrors LibrarySection so keyboard nav stays in sync.
function buildFlatHits(indexResults, enabled) {
  const out = []

  const algoIndex = (idx) => (indexResults[idx] && indexResults[idx].hits) || []
  const isSetOrQuestion = (h) => {
    const itemType = getDocumentType(h)
    return itemType === 'set' || itemType === 'question'
  }

  if (enabled.repos) {
    appendLibraryRows(out, 'a', algoIndex('repos'))
  }
  if (enabled.folders) {
    appendLibraryRows(out, 'a', algoIndex('folders'))
  }
  if (enabled.setsQuestions) {
    appendLibraryRows(out, 'a', algoIndex('sets_questions').filter(isSetOrQuestion))
  }

  return out
}

// True when the keyboard event happens inside an editable field (the search
// input, a contenteditable area, etc.) — in those cases ArrowUp/ArrowDown
// belong to the editor, not to the results list.
function isEditableTarget(target) {
  if (!target) return false
  const tag = (target.tagName || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

// True when the key event is aimed at a real button or link, which activates
// itself on Space/Enter (e.g. the "Show more results" button). The result rows
// are excluded on purpose: clicking one focuses it (role=button, tabIndex=0) and
// the list must keep owning Space/Enter for the row the user just clicked.
function isSelfActivatingTarget(target) {
  if (!target) return false
  const tag = (target.tagName || '').toUpperCase()
  return tag === 'BUTTON' || tag === 'A'
}

// react-redux v5 has no hooks, so the store-derived bits (previewItem flag +
// updatePreviewItem dispatcher) are injected via connect (see bottom of fn).
function ActiveResultProviderComponent({ children, previewItem, updatePreviewItem: dispatchUpdatePreviewItem }) {
  const { indexResults, currentRefinement } = useSearchContext()
  const { enabled } = useCategoryFilters()
  const { history } = useRouterHistory()
  // When the lightweight preview modal is open it owns the keyboard (arrows /
  // Esc / P / Q), so the results-page list handler must stand down.
  const isPreviewModalOpen = !!previewItem

  // When the query is empty there are no results to navigate or preview — force
  // an empty list so the right panel falls back to its empty state (otherwise a
  // stale active hit from the previous query keeps its preview mounted).
  const flatHits = React.useMemo(
    () => (currentRefinement ? buildFlatHits(indexResults, enabled) : []),
    [currentRefinement, indexResults, enabled],
  )

  const [activeIndex, setActiveIndex] = React.useState(0)

  // Reset to the first row whenever the query changes or the list shrinks.
  const flatIds = React.useMemo(() => flatHits.map((h) => h.id).join('|'), [flatHits])
  React.useEffect(() => {
    setActiveIndex(0)
  }, [currentRefinement, flatIds])

  // With an empty result set there are no rows to navigate or preview, so keep
  // the search input focused — the user is most likely still refining the query
  // (this also re-focuses after arriving from the dropdown, which blurs the
  // input on navigation so list arrow-keys can take over when results exist).
  const isEmptyResultSet = flatHits.length === 0
  React.useEffect(() => {
    if (!isEmptyResultSet) return
    const input = document.getElementById('searchInputField')
    if (input && document.activeElement !== input && typeof input.focus === 'function') {
      input.focus()
    }
  }, [isEmptyResultSet])

  const clampedIndex = flatHits.length === 0 ?
    -1 :
    Math.min(Math.max(activeIndex, 0), flatHits.length - 1)
  const activeRow = clampedIndex >= 0 ? flatHits[clampedIndex] : null

  React.useEffect(() => {
    function onKeyDown(e) {
      // The lightweight preview modal, when open, handles its own keys.
      if (isPreviewModalOpen) return
      // Block modifier combos; the active-row shortcuts are unmodified keys.
      if (e.altKey || e.ctrlKey || e.metaKey) return
      // ArrowDown while the search input is focused → move focus into the list
      // (re-focus the first row).
      const isSearchInput = e.target && e.target.id === 'searchInputField'
      if (isSearchInput && e.key === 'ArrowDown') {
        if (flatHits.length === 0) return
        e.preventDefault()
        if (typeof e.target.blur === 'function') e.target.blur()
        setActiveIndex(0)
        return
      }
      if (isEditableTarget(e.target) && e.key !== 'Enter') return
      if (flatHits.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((prev) => Math.min(prev + 1, flatHits.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        // ArrowUp from the first row → focus the search input.
        if (activeIndex <= 0) {
          const input = document.getElementById('searchInputField')
          if (input && typeof input.focus === 'function') input.focus()
          return
        }
        setActiveIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === ' ' || e.keyCode === 32) {
        // Space → lightweight preview modal for the active set/question row.
        // Swallowed for every row type: packs and folders have no modal preview,
        // but letting Space through would scroll the page out from under the
        // highlighted row.
        if (isEditableTarget(e.target) || isSelfActivatingTarget(e.target)) return
        const row = flatHits[Math.min(Math.max(activeIndex, 0), flatHits.length - 1)]
        if (!row) return
        e.preventDefault()
        const itemId = row.hit && (row.hit.id || row.hit._id)
        const isPreviewable = row.itemType === 'set' || row.itemType === 'question'
        if (isPreviewable && itemId) {
          dispatchUpdatePreviewItem(itemId, 'searchResults')
        }
      } else if (e.key === 'Enter') {
        // The search input also listens for Enter to blur itself — only
        // hijack when the user isn't typing into it.
        if (isEditableTarget(e.target)) return
        const row = flatHits[Math.min(Math.max(activeIndex, 0), flatHits.length - 1)]
        if (!row) return
        const itemId = row.hit && (row.hit.id || row.hit._id)
        const isPreviewable = row.itemType === 'set' || row.itemType === 'question'
        // Plain Enter → lightweight preview modal for local set/question rows;
        // for non-previewable rows (packs/folders/external) it opens/navigates.
        if (isPreviewable && itemId) {
          e.preventDefault()
          dispatchUpdatePreviewItem(itemId, 'searchResults')
          return
        }
        if (!row.path) return
        e.preventDefault()
        if (row.external) {
          trackHelpUse({
            source: 'searchResults',
            query: currentRefinement,
            type: 'externalLink',
            label: row.hit && row.hit.label,
            url: row.path,
          })
          window.open(row.path, '_blank', 'noopener,noreferrer')
        } else {
          trackContentUse({
            source: 'searchResults',
            query: currentRefinement,
            action: 'open',
            itemType: row.itemType,
            itemId,
          })
          history.push(row.path)
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [
    flatHits, activeIndex, history, dispatchUpdatePreviewItem,
    isPreviewModalOpen, currentRefinement,
  ])

  // Lets a mouse click on a row sync the active highlight (and therefore the
  // right-hand preview) to that row.
  const activateRow = React.useCallback((rowId) => {
    const idx = flatHits.findIndex((h) => h.id === rowId)
    if (idx >= 0) setActiveIndex(idx)
  }, [flatHits])

  const value = React.useMemo(() => ({
    activeId: activeRow ? activeRow.id : null,
    activeHit: activeRow ? activeRow.hit : null,
    activeItemType: activeRow ? activeRow.itemType : null,
    flatHits,
    activateRow,
  }), [activeRow, flatHits, activateRow])

  return (
    <ActiveResultContext.Provider value={value}>
      {children}
    </ActiveResultContext.Provider>
  )
}

const ActiveResultProvider = connect(
  (state) => ({ previewItem: state.previewItem }),
  { updatePreviewItem },
)(ActiveResultProviderComponent)

// Reads the active hit and feeds it to the right-column preview. Lives here
// (not in the JSX shell) because it needs access to the search contexts.
function ResultsRightPanel() {
  const { activeHit, activeItemType } = useActiveResult()
  return <SearchResultsPreviewPanel activeHit={activeHit} activeItemType={activeItemType} />
}

// ─── Keyboard shortcut help footer ────────────────────────────────────────────
// Mirrors the dropdown's bottom hints. Two behaviours:
//   - Focus- and type-aware: while the page search input is focused (the user is
//     typing) only the navigate hint shows, matching the dropdown which drops the
//     Enter hints when the input is focused. Otherwise the action hints describe
//     what the keys do to the highlighted row — preview + double-Enter open for
//     sets and questions, a plain Enter open for packs and folders.
//   - Viewport-pinned: when the results overflow the viewport the bar is pinned
//     to the bottom of the window (fixed, aligned to the center panel); when the
//     results fit it sits in flow under the last result.
function ResultsShortcutHelp() {
  const wrapRef = React.useRef(null)
  const barRef = React.useRef(null)
  const lastRef = React.useRef({
    pinned: false, left: -1, width: -1, barHeight: -1,
  })
  const [inputFocused, setInputFocused] = React.useState(false)
  const [pinned, setPinned] = React.useState(false)
  const [pinnedStyle, setPinnedStyle] = React.useState(null)
  const { currentRefinement, indexResults } = useSearchContext()
  const { activeItemType } = useActiveResult()
  // Packs and folders have no modal preview: Enter opens them, and Space is
  // swallowed (see the keydown handler), so only the open hint applies.
  const activeHasPreview = activeItemType === 'set' || activeItemType === 'question'
  const showActionHints = !inputFocused && !!activeItemType

  // Track focus of the page search input so the action hints hide while typing.
  React.useEffect(() => {
    const input = document.getElementById('searchInputField')
    if (!input) return undefined
    const onFocus = () => setInputFocused(true)
    const onBlur = () => setInputFocused(false)
    setInputFocused(document.activeElement === input)
    input.addEventListener('focus', onFocus)
    input.addEventListener('blur', onBlur)
    return () => {
      input.removeEventListener('focus', onFocus)
      input.removeEventListener('blur', onBlur)
    }
  }, [])

  // Decide pinned vs inline. The wrapper always reserves the bar's height (so
  // pinning doesn't change the document height and can't oscillate). Pin when
  // the wrapper's natural position is below the viewport fold; once the user
  // scrolls to it (results end), it unpins and sits under the last result.
  const measure = React.useCallback(() => {
    const wrap = wrapRef.current
    const bar = barRef.current
    if (!wrap || !bar) return
    const barHeight = bar.offsetHeight || 0
    const rect = wrap.getBoundingClientRect()
    const shouldPin = (rect.top + barHeight) > window.innerHeight
    const next = {
      pinned: shouldPin,
      left: shouldPin ? Math.round(rect.left) : -1,
      width: shouldPin ? Math.round(rect.width) : -1,
      barHeight: shouldPin ? barHeight : -1,
    }
    const prev = lastRef.current
    if (
      next.pinned === prev.pinned && next.left === prev.left &&
      next.width === prev.width && next.barHeight === prev.barHeight
    ) return
    lastRef.current = next
    setPinned(next.pinned)
    setPinnedStyle(next.pinned ?
      { left: next.left, width: next.width, height: next.barHeight } :
      null)
  }, [])

  React.useEffect(() => {
    let raf = null
    const schedule = () => {
      if (raf) return
      raf = window.requestAnimationFrame(() => { raf = null; measure() })
    }
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    return () => {
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [measure])

  // Re-measure when the results (which grow/shrink the list) or the focus-driven
  // hint set change. measure() is internally guarded, so the setState it may run
  // doesn't re-trigger this effect (its deps are unchanged) — no loop.
  React.useLayoutEffect(
    () => { measure() },
    [measure, currentRefinement, indexResults, inputFocused, activeItemType],
  )

  return (
    <div
      ref={wrapRef}
      className='searchResults-shortcutHelpWrap'
      style={pinned && pinnedStyle ? { minHeight: pinnedStyle.height } : undefined}
    >
      <div
        ref={barRef}
        className={`searchResults-shortcutHelp${pinned ? ' searchResults-shortcutHelp--pinned' : ''}`}
        style={pinned && pinnedStyle ? { left: pinnedStyle.left, width: pinnedStyle.width } : undefined}
      >
        <span className='searchResults-shortcutHelp-item'>
          <span className='searchResults-shortcutHelp-keys'>
            <kbd>↑↓</kbd>
          </span>
          navigate
        </span>
        {showActionHints && activeHasPreview && (
          <React.Fragment>
            <span className='searchResults-shortcutHelp-item'>
              <span className='searchResults-shortcutHelp-keys'>
                <kbd>Enter</kbd>
                <span className='searchResults-shortcutHelp-sep'>/</span>
                <kbd>Space</kbd>
              </span>
              preview
            </span>
            <span className='searchResults-shortcutHelp-item'>
              <span className='searchResults-shortcutHelp-keys'>
                <kbd>Enter</kbd>
                <kbd>Enter</kbd>
              </span>
              open
            </span>
          </React.Fragment>
        )}
        {showActionHints && !activeHasPreview && (
          <span className='searchResults-shortcutHelp-item'>
            <span className='searchResults-shortcutHelp-keys'>
              <kbd>Enter</kbd>
            </span>
            open
          </span>
        )}
      </div>
    </div>
  )
}

// ─── SearchResultsContainer ───────────────────────────────────────────────────

class SearchResultsContainer extends Component {
  constructor(props) {
    super(props)
    this.onSearchStateChange = this.onSearchStateChange.bind(this)
    this.toggleCategory = this.toggleCategory.bind(this)
    this.state = {
      searchState: urlToSearchState(props.location),
      enabledCategories: {
        repos: true, folders: true, setsQuestions: true,
      },
    }
  }

  componentDidMount() {
    analytics.page('Search Results')
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (props.location !== this.props.location) {
      this.setState({ searchState: urlToSearchState(props.location) })
    }
  }

  componentDidUpdate(prevProps) {
    // Re-focus the page search input when we navigate to an empty-query results
    // page. The in-tree empty-state focus effect only fires when the empty
    // state is first entered; a results->results re-navigation with an empty
    // query (e.g. Cmd+K, Enter from this page) doesn't change that flag, and
    // the top-nav input was just blurred — so nothing would be focused.
    if (prevProps.location === this.props.location) return
    const { query } = urlToSearchState(this.props.location) || {}
    if (query) return
    window.setTimeout(() => {
      const input = document.getElementById('searchInputField')
      if (input && document.activeElement !== input && typeof input.focus === 'function') {
        input.focus()
      }
    }, 0)
  }

  onSearchStateChange(searchState) {
    clearTimeout(this.debouncedSetState)
    this.debouncedSetState = setTimeout(() => {
      window.history.pushState(
        searchState,
        null,
        searchStateToUrl(this.props, searchState),
      )
    }, updateAfter)
    this.setState({ searchState })
  }

  toggleCategory(key) {
    this.setState((prev) => ({
      enabledCategories: {
        ...prev.enabledCategories,
        [key]: !prev.enabledCategories[key],
      },
    }))
  }

  render() {
    const { showSectionSelectionModal: playCb } = this.props
    const categoryFiltersValue = {
      enabled: this.state.enabledCategories,
      onToggle: this.toggleCategory,
    }
    return (
      <DocumentTitle title='Search Results - Plickers'>
        <div className='page searchResults page--wideLayout'>
          <div className='page-leftPanel' />
          <SearchProviderContainer
            searchState={this.state.searchState}
            onSearchStateChange={this.onSearchStateChange}
            createURL={createURL}
            hitsPerPage={100}
          >
            <RouterHistoryBridge>
              <CategoryFiltersContext.Provider value={categoryFiltersValue}>
                <ActiveResultProvider>
                  <div className='page-centerPanel'>
                    <ResultsPageHeader />
                    <div className='searchResults-content'>
                      <LibrarySection play={playCb} />
                    </div>
                    <ResultsShortcutHelp />
                  </div>
                  <div className='page-rightPanel'>
                    <ResultsRightPanel />
                  </div>
                </ActiveResultProvider>
              </CategoryFiltersContext.Provider>
            </RouterHistoryBridge>
          </SearchProviderContainer>
        </div>
      </DocumentTitle>
    )
  }
}

export default withRouter(connect(
  (state) => ({
    permissions: state.permissions,
  }),
  { showSectionSelectionModal },
)(SearchResultsContainer))
